import { useEffect, useRef, useCallback, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, History } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { uuid } from '@/lib/utils';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import { useEditorInput, type RoomZone } from '@/hooks/useEditorInput';
import { EditorToolbar } from '@/components/dungeon-editor/EditorToolbar';
import { EditorCanvas } from '@/components/dungeon-editor/EditorCanvas';
import { EditorPanel } from '@/components/dungeon-editor/EditorPanel';
import { EntityPickerModal } from '@/components/dungeon-editor/modals/EntityPickerModal';
import { PlaytestMode } from '@/components/dungeon-editor/PlaytestMode';
import { createEmptyMapData } from '@/lib/db/dungeon-types';
import type { DungeonMapData, DungeonRoom, DungeonCorridor } from '@/lib/db/dungeon-types';

interface DungeonResponse {
  id: string;
  name: string;
  description: string;
  theme: string;
  status: 'draft' | 'published';
  mapData: DungeonMapData;
}

// ---------------------------------------------------------------------------
// Corridor path helper
// ---------------------------------------------------------------------------
function computeCorridorPath(
  from: DungeonRoom,
  to: DungeonRoom,
): { x: number; y: number }[] {
  const fromCx = Math.floor(from.x + from.width / 2);
  const fromCy = Math.floor(from.y + from.height / 2);
  const toCx = Math.floor(to.x + to.width / 2);
  const toCy = Math.floor(to.y + to.height / 2);

  const cells: { x: number; y: number }[] = [];
  const seen = new Set<string>();

  const isInsideRoom = (x: number, y: number, room: DungeonRoom) =>
    x >= room.x && x < room.x + room.width && y >= room.y && y < room.y + room.height;

  const addCell = (x: number, y: number) => {
    if (isInsideRoom(x, y, from) || isInsideRoom(x, y, to)) return;
    const key = `${x},${y}`;
    if (seen.has(key)) return;
    seen.add(key);
    cells.push({ x, y });
  };

  // Go horizontal first, then vertical (L-shaped)
  const stepX = toCx > fromCx ? 1 : -1;
  const stepY = toCy > fromCy ? 1 : -1;

  // Horizontal segment from fromCx to toCx at fromCy
  let x = fromCx;
  while (x !== toCx) {
    addCell(x, fromCy);
    x += stepX;
  }

  // Vertical segment from fromCy to toCy at toCx
  let y = fromCy;
  while (y !== toCy) {
    addCell(toCx, y);
    y += stepY;
  }

  // Final cell at destination center
  addCell(toCx, toCy);

  return cells;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function DungeonEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const meta = useDungeonEditorStore((s) => s.meta);
  const mapData = useDungeonEditorStore((s) => s.mapData);
  const isDirty = useDungeonEditorStore((s) => s.isDirty);
  const isPlaytesting = useDungeonEditorStore((s) => s.isPlaytesting);
  const selectedTool = useDungeonEditorStore((s) => s.selectedTool);
  const setDungeon = useDungeonEditorStore((s) => s.setDungeon);
  const reset = useDungeonEditorStore((s) => s.reset);
  const markClean = useDungeonEditorStore((s) => s.markClean);
  const undo = useDungeonEditorStore((s) => s.undo);
  const redo = useDungeonEditorStore((s) => s.redo);
  const selectRoom = useDungeonEditorStore((s) => s.selectRoom);
  const selectEntity = useDungeonEditorStore((s) => s.selectEntity);
  const selectTrigger = useDungeonEditorStore((s) => s.selectTrigger);
  const addRoom = useDungeonEditorStore((s) => s.addRoom);
  const addCorridor = useDungeonEditorStore((s) => s.addCorridor);
  const addEntity = useDungeonEditorStore((s) => s.addEntity);
  const addTrigger = useDungeonEditorStore((s) => s.addTrigger);
  const updateCorridor = useDungeonEditorStore((s) => s.updateCorridor);
  const removeRoom = useDungeonEditorStore((s) => s.removeRoom);
  const removeEntity = useDungeonEditorStore((s) => s.removeEntity);
  const removeTrigger = useDungeonEditorStore((s) => s.removeTrigger);
  const removeCorridor = useDungeonEditorStore((s) => s.removeCorridor);
  const clearSelection = useDungeonEditorStore((s) => s.clearSelection);
  const pushUndo = useDungeonEditorStore((s) => s.pushUndo);
  const setMapData = useDungeonEditorStore((s) => s.setMapData);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [entityPickerCell, setEntityPickerCell] = useState<{ x: number; y: number } | null>(null);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirtyRef = useRef(isDirty);
  const metaRef = useRef(meta);
  const mapDataRef = useRef(mapData);
  const corridorStartRoomRef = useRef<string | null>(null);
  const dragStartCell = useRef<{ x: number; y: number } | null>(null);

  // Keep refs in sync so save callbacks don't capture stale closure values
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { metaRef.current = meta; }, [meta]);
  useEffect(() => { mapDataRef.current = mapData; }, [mapData]);

  // Load dungeon on mount
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setLoadError(null);

    apiFetch(`/api/admin/dungeons/${id}`)
      .then((res) => res.json() as Promise<{ dungeon: DungeonResponse }>)
      .then(({ dungeon }) => {
        const defaults = createEmptyMapData(60, 45);
        const normalizedMapData: DungeonMapData = {
          ...defaults,
          ...dungeon.mapData,
        };
        setDungeon(
          {
            id: dungeon.id,
            name: dungeon.name,
            description: dungeon.description,
            theme: dungeon.theme,
            status: dungeon.status,
          },
          normalizedMapData
        );
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Failed to load dungeon');
      })
      .finally(() => setLoading(false));

    return () => {
      reset();
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save function
  const save = useCallback(async () => {
    const currentMeta = metaRef.current;
    const currentMapData = mapDataRef.current;
    if (!id || !currentMeta) return;

    setSaving(true);
    try {
      await apiFetch(`/api/admin/dungeons/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentMeta.name,
          description: currentMeta.description,
          theme: currentMeta.theme,
          mapData: currentMapData,
        }),
      });
      markClean();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [id, markClean]);

  // Auto-save: debounced 2s after isDirty becomes true
  useEffect(() => {
    if (!isDirty) return;

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(() => {
      if (isDirtyRef.current) {
        save();
      }
    }, 2000);

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [isDirty, save]);

  // Snapshot / history save
  const handleSnapshot = useCallback(async () => {
    const currentMeta = metaRef.current;
    const currentMapData = mapDataRef.current;
    if (!id || !currentMeta) return;

    const label = window.prompt('Snapshot label (optional):') ?? '';

    setSaving(true);
    try {
      await apiFetch(`/api/admin/dungeons/${id}/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: label || undefined,
          mapData: currentMapData,
        }),
      });
    } catch (err) {
      console.error('Snapshot failed:', err);
    } finally {
      setSaving(false);
    }
  }, [id]);

  // ---------------------------------------------------------------------------
  // Interaction handlers
  // ---------------------------------------------------------------------------

  const handleCellClick = useCallback(
    (x: number, y: number) => {
      const currentMapData = mapDataRef.current;

      if (selectedTool === 'select') {
        clearSelection();
        return;
      }

      if (selectedTool === 'door') {
        // Find corridor whose cells contain this cell
        const doorOrder: Array<DungeonCorridor['doorType']> = [null, 'door', 'locked', 'hidden', 'gate'];
        const corridor = currentMapData.corridors.find((c) =>
          c.cells.some((cell) => cell.x === x && cell.y === y)
        );
        if (corridor) {
          const currentIndex = doorOrder.indexOf(corridor.doorType ?? null);
          const nextIndex = (currentIndex + 1) % doorOrder.length;
          updateCorridor(corridor.id, { doorType: doorOrder[nextIndex] });
        }
        return;
      }

      if (selectedTool === 'entity') {
        const room = currentMapData.rooms.find(
          (r) => x >= r.x && x < r.x + r.width && y >= r.y && y < r.y + r.height
        );
        if (room) {
          setEntityPickerCell({ x, y });
        }
        return;
      }

      if (selectedTool === 'trigger') {
        const room = currentMapData.rooms.find(
          (r) => x >= r.x && x < r.x + r.width && y >= r.y && y < r.y + r.height
        );
        const newTrigger = {
          id: uuid(),
          name: 'New Trigger',
          condition: 'step' as const,
          effect: 'custom' as const,
          effectData: {},
          requiredFlags: [],
          oneShot: false,
          targetX: x,
          targetY: y,
          roomId: room?.id,
        };
        addTrigger(newTrigger);
        selectTrigger(newTrigger.id);
        return;
      }

      if (selectedTool === 'erase') {
        // Priority: entity → trigger → corridor → room
        const entity = currentMapData.entities.find((e) => e.x === x && e.y === y);
        if (entity) {
          removeEntity(entity.id);
          return;
        }

        const trigger = currentMapData.triggers.find(
          (t) => t.targetX === x && t.targetY === y
        );
        if (trigger) {
          removeTrigger(trigger.id);
          return;
        }

        const corridor = currentMapData.corridors.find((c) =>
          c.cells.some((cell) => cell.x === x && cell.y === y)
        );
        if (corridor) {
          removeCorridor(corridor.id);
          return;
        }

        const room = currentMapData.rooms.find(
          (r) => x >= r.x && x < r.x + r.width && y >= r.y && y < r.y + r.height
        );
        if (room) {
          removeRoom(room.id);
          return;
        }
      }
    },
    [
      selectedTool,
      clearSelection,
      updateCorridor,
      addTrigger,
      selectTrigger,
      removeEntity,
      removeTrigger,
      removeCorridor,
      removeRoom,
    ]
  );

  const handleDragStart = useCallback((x: number, y: number) => {
    dragStartCell.current = { x, y };
  }, []);

  const handleDragEnd = useCallback(
    (x: number, y: number) => {
      if (selectedTool === 'room' && dragStartCell.current) {
        const start = dragStartCell.current;
        const rx = Math.min(start.x, x);
        const ry = Math.min(start.y, y);
        const rw = Math.abs(x - start.x) + 1;
        const rh = Math.abs(y - start.y) + 1;

        if (rw >= 2 && rh >= 2) {
          addRoom({
            id: uuid(),
            name: 'New Room',
            purpose: '',
            zone: 0,
            x: rx,
            y: ry,
            width: rw,
            height: rh,
            description: '',
            atmosphere: '',
            secrets: '',
          });
        }
      }
      dragStartCell.current = null;
    },
    [selectedTool, addRoom]
  );

  const handleRoomClick = useCallback(
    (roomId: string) => {
      const currentMapData = mapDataRef.current;

      if (selectedTool === 'corridor') {
        if (!corridorStartRoomRef.current) {
          // Start the corridor from this room
          corridorStartRoomRef.current = roomId;
          selectRoom(roomId);
        } else if (corridorStartRoomRef.current !== roomId) {
          // Complete the corridor to this room
          const fromRoom = currentMapData.rooms.find(
            (r) => r.id === corridorStartRoomRef.current
          );
          const toRoom = currentMapData.rooms.find((r) => r.id === roomId);
          if (fromRoom && toRoom) {
            const cells = computeCorridorPath(fromRoom, toRoom);
            if (cells.length > 0) {
              addCorridor({
                id: uuid(),
                fromRoomId: fromRoom.id,
                toRoomId: toRoom.id,
                cells,
                doorType: null,
              });
            }
          }
          corridorStartRoomRef.current = null;
          clearSelection();
        } else {
          // Clicked the same room — cancel
          corridorStartRoomRef.current = null;
          clearSelection();
        }
        return;
      }

      selectRoom(roomId);
    },
    [selectedTool, selectRoom, addCorridor, clearSelection]
  );

  const handleEntityClick = useCallback(
    (entityId: string) => {
      selectEntity(entityId);
    },
    [selectEntity]
  );

  const handleRoomDoubleClick = useCallback(
    (roomId: string) => {
      selectRoom(roomId);
    },
    [selectRoom],
  );

  const handleEntityDoubleClick = useCallback(
    (entityId: string) => {
      selectEntity(entityId);
    },
    [selectEntity],
  );

  const handleRoomDragStart = useCallback(
    (_roomId: string, _zone: RoomZone) => {
      pushUndo();
    },
    [pushUndo],
  );

  const handleRoomDrag = useCallback(
    (roomId: string, zone: RoomZone, dx: number, dy: number) => {
      // Read directly from store for latest state (mapDataRef may be stale mid-drag)
      const currentMapData = useDungeonEditorStore.getState().mapData;
      const room = currentMapData.rooms.find((r) => r.id === roomId);
      if (!room) return;

      let newMapData: DungeonMapData;

      if (zone === 'move') {
        const newX = Math.max(0, room.x + dx);
        const newY = Math.max(0, room.y + dy);
        const actualDx = newX - room.x;
        const actualDy = newY - room.y;
        if (actualDx === 0 && actualDy === 0) return;

        const updatedRooms = currentMapData.rooms.map((r) =>
          r.id === roomId ? { ...r, x: newX, y: newY } : r,
        );
        const updatedEntities = currentMapData.entities.map((e) =>
          e.roomId === roomId ? { ...e, x: e.x + actualDx, y: e.y + actualDy } : e,
        );
        const updatedTriggers = currentMapData.triggers.map((t) =>
          t.roomId === roomId && t.targetX !== undefined && t.targetY !== undefined
            ? { ...t, targetX: t.targetX + actualDx, targetY: t.targetY + actualDy }
            : t,
        );

        newMapData = {
          ...currentMapData,
          rooms: updatedRooms,
          entities: updatedEntities,
          triggers: updatedTriggers,
        };
      } else {
        let newX = room.x;
        let newY = room.y;
        let newW = room.width;
        let newH = room.height;

        if (zone.includes('n')) { newY += dy; newH -= dy; }
        if (zone.includes('s')) { newH += dy; }
        if (zone.includes('w')) { newX += dx; newW -= dx; }
        if (zone.includes('e')) { newW += dx; }

        if (newW < 2) { if (zone.includes('w')) { newX = room.x + room.width - 2; } newW = 2; }
        if (newH < 2) { if (zone.includes('n')) { newY = room.y + room.height - 2; } newH = 2; }

        if (newX < 0) { newW += newX; newX = 0; }
        if (newY < 0) { newH += newY; newY = 0; }

        const updatedRooms = currentMapData.rooms.map((r) =>
          r.id === roomId ? { ...r, x: newX, y: newY, width: newW, height: newH } : r,
        );

        const updatedEntities = currentMapData.entities.map((e) => {
          if (e.roomId !== roomId) return e;
          const cx = Math.max(newX, Math.min(newX + newW - 1, e.x));
          const cy = Math.max(newY, Math.min(newY + newH - 1, e.y));
          return cx !== e.x || cy !== e.y ? { ...e, x: cx, y: cy } : e;
        });

        const updatedTriggers = currentMapData.triggers.map((t) => {
          if (t.roomId !== roomId || t.targetX === undefined || t.targetY === undefined) return t;
          const tx = Math.max(newX, Math.min(newX + newW - 1, t.targetX));
          const ty = Math.max(newY, Math.min(newY + newH - 1, t.targetY));
          return tx !== t.targetX || ty !== t.targetY ? { ...t, targetX: tx, targetY: ty } : t;
        });

        newMapData = {
          ...currentMapData,
          rooms: updatedRooms,
          entities: updatedEntities,
          triggers: updatedTriggers,
        };
      }

      // Recompute corridors connected to this room
      const updatedCorridors = newMapData.corridors.map((c) => {
        if (c.fromRoomId !== roomId && c.toRoomId !== roomId) return c;
        const fromRoom = newMapData.rooms.find((r) => r.id === c.fromRoomId);
        const toRoom = newMapData.rooms.find((r) => r.id === c.toRoomId);
        if (!fromRoom || !toRoom) return c;
        const cells = computeCorridorPath(fromRoom, toRoom);
        return { ...c, cells };
      });

      newMapData = { ...newMapData, corridors: updatedCorridors };
      setMapData(newMapData);
    },
    [setMapData],
  );

  const handleRoomDragEnd = useCallback(
    (_roomId: string) => {
      // Drag is complete — undo was pushed at start, mapData is already updated
    },
    [],
  );

  const { canvasHandlers, attachWheel, cursor, dragPreview } = useEditorInput(
    {
      selectedTool,
      zoom: useDungeonEditorStore((s) => s.zoom),
      panOffset: useDungeonEditorStore((s) => s.panOffset),
      mapData,
      selectedRoomId: useDungeonEditorStore((s) => s.selectedRoomId),
      selectedEntityId: useDungeonEditorStore((s) => s.selectedEntityId),
      selectedTriggerId: useDungeonEditorStore((s) => s.selectedTriggerId),
    },
    {
      setTool: useDungeonEditorStore((s) => s.setTool),
      setZoom: useDungeonEditorStore((s) => s.setZoom),
      setPanOffset: useDungeonEditorStore((s) => s.setPanOffset),
      undo,
      redo,
      save,
      selectRoom,
      selectEntity,
      selectTrigger,
      clearSelection,
      removeRoom,
      removeEntity,
      removeTrigger,
      removeCorridor,
      onCellClick: handleCellClick,
      onCellDragStart: handleDragStart,
      onCellDragEnd: handleDragEnd,
      onRoomClick: handleRoomClick,
      onRoomDoubleClick: handleRoomDoubleClick,
      onEntityClick: handleEntityClick,
      onEntityDoubleClick: handleEntityDoubleClick,
      onRoomDragStart: handleRoomDragStart,
      onRoomDrag: handleRoomDrag,
      onRoomDragEnd: handleRoomDragEnd,
    },
  );

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-gray-400">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span>Loading dungeon…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-950 text-gray-400 gap-4">
        <p className="text-red-400 text-sm">{loadError}</p>
        <button
          onClick={() => navigate('/admin/dungeons')}
          className="text-sm underline hover:text-white"
        >
          Back to dungeons
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-950 text-gray-100">
      {/* Top bar */}
      <header className="h-11 flex items-center gap-3 px-3 bg-gray-900 border-b border-gray-700 shrink-0">
        <Link
          to="/admin/dungeons"
          className="text-gray-400 hover:text-white transition-colors"
          title="Back to dungeons"
        >
          <ArrowLeft size={18} />
        </Link>

        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-sm truncate">{meta?.name ?? 'Untitled'}</span>
          {meta?.theme && (
            <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded shrink-0">
              {meta.theme}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto shrink-0">
          {isDirty && !saving && (
            <span className="text-xs text-yellow-500">unsaved</span>
          )}
          {saving && (
            <Loader2 className="animate-spin text-gray-400" size={14} />
          )}

          <button
            title="Save snapshot to history"
            onClick={handleSnapshot}
            disabled={saving}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <History size={14} />
            <span>Snapshot</span>
          </button>

          <button
            title="Save (Ctrl+S)"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50"
          >
            <Save size={14} />
            <span>Save</span>
          </button>
        </div>
      </header>

      {/* Main area */}
      <div className="flex flex-1 min-h-0 relative">
        <EditorToolbar />

        <EditorCanvas
          canvasHandlers={canvasHandlers}
          attachWheel={attachWheel}
          cursor={cursor}
          dragPreview={dragPreview}
        />

        <EditorPanel />

        {isPlaytesting && <PlaytestMode />}
      </div>

      {entityPickerCell && (
        <EntityPickerModal
          onSelectMonster={(monster) => {
            const currentMapData = mapDataRef.current;
            const room = currentMapData.rooms.find(
              (r) =>
                entityPickerCell.x >= r.x &&
                entityPickerCell.x < r.x + r.width &&
                entityPickerCell.y >= r.y &&
                entityPickerCell.y < r.y + r.height
            );
            addEntity({
              id: uuid(),
              roomId: room?.id ?? '',
              catalogEntryId: monster.id,
              type: 'monster',
              name: monster.name,
              icon: monster.icon,
              x: entityPickerCell.x,
              y: entityPickerCell.y,
              metadata: {
                difficulty: monster.difficulty,
                hp: monster.hp,
                resolve: monster.resolve,
              },
            });
            setEntityPickerCell(null);
          }}
          onSelectCustom={(type, name, icon) => {
            const currentMapData = mapDataRef.current;
            const room = currentMapData.rooms.find(
              (r) =>
                entityPickerCell.x >= r.x &&
                entityPickerCell.x < r.x + r.width &&
                entityPickerCell.y >= r.y &&
                entityPickerCell.y < r.y + r.height
            );
            addEntity({
              id: uuid(),
              roomId: room?.id ?? '',
              type,
              name,
              icon,
              x: entityPickerCell.x,
              y: entityPickerCell.y,
            });
            setEntityPickerCell(null);
          }}
          onClose={() => setEntityPickerCell(null)}
        />
      )}
    </div>
  );
}

